import React, { useEffect, useRef, useState } from "react";
import { Form } from "react-bootstrap";
import CustomTooltip from "../Tooltip";
import CustomTagsInput from "../../CommonComponents/Tags/CustomTagsInput";
import { networkService } from "../../config/axios";
import { csrfToken } from "../../../utils/csrfTokenUtils";

const hasValue = (v) => v !== "" && v !== null && v !== undefined;

const SendNotificationTab = ({
  editLaserLinkItem,
  updateEditLaserLinkData,
  getDocFiles,
  errorListData,
  setIsError,
}) => {
  const receipientTypeViewNotify = useRef(null);
  const receipientTypeViewNotifyPage = useRef(null);
  const [formErrorData, setFormErrorData] = useState([]);
  const [specificEmails, setSpecificEmails] = useState([]);
  const [emailOptions, setEmailOptions] = useState([]);
  const [filteredEmailOptions, setFilteredEmailOptions] = useState([]);
  const [emailQuery, setEmailQuery] = useState("");

  const fetchEmails = async () => {
    const response = await networkService("get-users.json", "POST", {
      authenticity_token: csrfToken(),
      field: "current_user",
      value: true,
    });

    const normalizedOptions = (response.data || [])
      .map((item) => {
        if (typeof item === "string") return { email: item };
        if (item?.email) return { email: item.email };
        return null;
      })
      .filter((item) => item?.email);

    setEmailOptions(normalizedOptions);

    if (
      !Array.isArray(editLaserLinkItem?.notification_emails) ||
      editLaserLinkItem.notification_emails.length === 0
    ) {
      setSpecificEmails(normalizedOptions);
      updateEditLaserLinkData({
        ...editLaserLinkItem,
        notification_emails: normalizedOptions,
      });
    }
  };

  useEffect(() => {
    if (
      !Array.isArray(editLaserLinkItem?.notification_emails) ||
      editLaserLinkItem.notification_emails.length === 0
    ) {
      return;
    }

    const normalized = editLaserLinkItem.notification_emails
      .map((item) => {
        if (typeof item === "string") return { email: item };
        if (item?.email) return { email: item.email };
        return null;
      })
      .filter((item) => item?.email);

    setSpecificEmails(normalized);
  }, [editLaserLinkItem?.notification_emails, editLaserLinkItem?.id]);

  useEffect(() => {
    if (!emailQuery || emailQuery.trim() === "") {
      setFilteredEmailOptions([]);
      return;
    }
    const search = emailQuery.trim().toLowerCase();
    const selectedSet = new Set(
      (specificEmails || [])
        .map((item) =>
          typeof item === "string"
            ? item.toLowerCase()
            : item?.email?.toLowerCase()
        )
        .filter(Boolean)
    );
    const suggestions = emailOptions.filter((option) => {
      const optionEmail = option?.email?.toLowerCase();
      if (!optionEmail) return false;
      if (selectedSet.has(optionEmail)) return false;
      return optionEmail.includes(search);
    });
    setFilteredEmailOptions(suggestions.slice(0, 10));
  }, [emailQuery, emailOptions, specificEmails]);

  const addNotificationEmail = (emailValue) => {
    const normalizedEmail =
      typeof emailValue === "string"
        ? emailValue.trim()
        : emailValue?.email?.trim();
    if (!normalizedEmail) return;

    const lowerEmail = normalizedEmail.toLowerCase();
    const existing = (specificEmails || []).some((item) => {
      if (typeof item === "string") return item.toLowerCase() === lowerEmail;
      return item?.email?.toLowerCase() === lowerEmail;
    });

    if (existing) {
      setEmailQuery("");
      setFilteredEmailOptions([]);
      return;
    }

    const updatedEmails = [
      ...(specificEmails || []).map((item) =>
        typeof item === "string" ? { email: item } : item
      ),
      { email: normalizedEmail },
    ];

    setSpecificEmails(updatedEmails);
    updateEditLaserLinkData({
      ...editLaserLinkItem,
      notification_emails: updatedEmails,
    });

    setEmailOptions((prev) => {
      const alreadyInOptions = prev.some(
        (option) => option.email?.toLowerCase() === lowerEmail
      );
      return alreadyInOptions ? prev : [...prev, { email: normalizedEmail }];
    });

    setEmailQuery("");
    setFilteredEmailOptions([]);
  };

  useEffect(() => {
    fetchEmails();
  }, []);

  // Sync formErrorData with errorListData for validation-related fields (UI feedback)
  useEffect(() => {
    const validationErrors = [
      "notify_after",
      "notify_for_page",
      "notify_page_after",
    ];
    const newFormErrors = validationErrors.filter((err) =>
      errorListData?.includes(err)
    );

    setFormErrorData((prev) => {
      const currentFormErrors = prev.filter((err) =>
        validationErrors.includes(err)
      );
      if (
        newFormErrors.length !== currentFormErrors.length ||
        !newFormErrors.every((err) => currentFormErrors.includes(err))
      ) {
        return [
          ...prev.filter((err) => !validationErrors.includes(err)),
          ...newFormErrors,
        ];
      }
      return prev;
    });
  }, [errorListData]);

  return (
    <div className="custom-tab-data">
      <div className="d-flex mb-2">
        <label className="send-mail-notify">
          Send email notification
          <Form.Check
            className="laser-setting-popup d-inline-block kdf-insert"
            type="switch"
            id="email-notification-switch"
            name="send_engagement_notif"
            checked={
              editLaserLinkItem?.send_engagement_notif != undefined &&
              editLaserLinkItem?.send_engagement_notif === true
                ? editLaserLinkItem?.send_engagement_notif
                : false
            }
            onChange={() =>
              updateEditLaserLinkData({
                ...editLaserLinkItem,
                send_engagement_notif:
                  !editLaserLinkItem?.send_engagement_notif,
              })
            }
          />
        </label>
      </div>
      {!!editLaserLinkItem?.send_engagement_notif && (
        <>
          <div className="align-items-center laserLINK-email-custom-tags TO-mail">
            <CustomTagsInput
              targetKey="email"
              tags={specificEmails || []}
              placeholderText="Enter email"
              handleAddTags={(value) => {
                addNotificationEmail(value);
              }}
              handleRemoveTags={(value) => {
                const normalized = (value || [])
                  .map((item) =>
                    typeof item === "string" ? { email: item } : item
                  )
                  .filter((item) => item?.email);

                setSpecificEmails(normalized);
                updateEditLaserLinkData({
                  ...editLaserLinkItem,
                  notification_emails: normalized,
                  ...(normalized.length === 0 && { send_engagement_notif: false }),
                });
              }}
              isEmail={true}
              triggerKeyDownEvent={(value) => {
                setEmailQuery(value || "");
              }}
            />
            {filteredEmailOptions.length > 0 && emailQuery.trim() !== "" && (
              <ul className="email-suggestion-list">
                {filteredEmailOptions.map((option, index) => (
                  <li
                    key={option.email ?? index}
                    onClick={() => addNotificationEmail(option.email)}
                    className="cursor-pointer"
                  >
                    {option.email}
                  </li>
                ))}
              </ul>
            )}
          </div>
          {Array.isArray(editLaserLinkItem?.notification_emails) &&
            editLaserLinkItem.notification_emails.length > 0 && (
              <div>
                <p className="mt-4 mb-2 rec-whe">When the recipient:</p>
                <div className="d-flex cursor-pointer ">
                  <Form.Check
                    className="laser-setting-popup kdf-view-second"
                    inline
                    name="notify_when"
                    type="radio"
                    id="notify-kdf-radio-notify_when"
                    value="notify_when"
                    checked={
                      editLaserLinkItem?.receipient_type === "notify_when"
                    }
                    onChange={(e) => {
                      if (e.target.value === "notify_when") {
                        const notifyAfter = editLaserLinkItem?.notify_after;
                        const defaultValue =
                          !notifyAfter || Number(notifyAfter) < 1
                            ? 22
                            : notifyAfter;
                        updateEditLaserLinkData({
                          ...editLaserLinkItem,
                          receipient_type: e.target.value,
                          notify_after: defaultValue,
                          notify_for_page: "",
                          notify_page_after: "",
                        });
                        setIsError?.(false);
                        setFormErrorData((prev) =>
                          prev.filter((it) => it !== "notify_after")
                        );
                        receipientTypeViewNotify.current?.focus();
                      } else {
                        updateEditLaserLinkData({
                          ...editLaserLinkItem,
                          receipient_type: e.target.value,
                        });
                      }
                    }}
                    label={
                      <span className="cursor-pointer ">
                        views this KDF for more than &nbsp;
                        <CustomTooltip
                          title={
                            <p className="p-1 text-light">
                              Please enter the time in seconds
                            </p>
                          }
                        >
                          <input
                            className={`edit-box-input ${
                              formErrorData?.includes("notify_after")
                                ? "box-input-error"
                                : ""
                            }`}
                            type="number"
                            min="1"
                            value={editLaserLinkItem?.notify_after ?? ""}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              if (
                                inputValue === "" ||
                                Number(inputValue) >= 1
                              ) {
                                const numValue =
                                  inputValue === ""
                                    ? ""
                                    : Number(inputValue);
                                updateEditLaserLinkData({
                                  ...editLaserLinkItem,
                                  receipient_type: "notify_when",
                                  notify_after: numValue,
                                  notify_for_page: "",
                                  notify_page_after: "",
                                });
                                if (
                                  numValue !== "" &&
                                  Number(numValue) >= 1
                                ) {
                                  setIsError?.(false);
                                  setFormErrorData((prev) =>
                                    prev.filter(
                                      (it) => it !== "notify_after"
                                    )
                                  );
                                }
                              }
                            }}
                            onBlur={(e) => {
                              const inputValue = e.target.value;
                              if (
                                editLaserLinkItem?.receipient_type ===
                                "notify_when"
                              ) {
                                if (
                                  inputValue === "" ||
                                  Number(inputValue) < 1
                                ) {
                                  updateEditLaserLinkData({
                                    ...editLaserLinkItem,
                                    notify_after: 22,
                                  });
                                  setIsError?.(false);
                                  setFormErrorData((prev) =>
                                    prev.filter(
                                      (it) => it !== "notify_after"
                                    )
                                  );
                                }
                              }
                            }}
                            ref={receipientTypeViewNotify}
                          />
                        </CustomTooltip> &nbsp;
                        seconds
                        {/* Empty required fields only get a red border; the message is for an entered-but-invalid value. */}
                        {formErrorData?.includes("notify_after") && hasValue(editLaserLinkItem?.notify_after) && (
                          <small className="text-danger notify-error-msg">
                            Must be at least 1 second.
                          </small>
                        )}
                        <CustomTooltip
                          placement="left"
                          title={
                            <p>
                              Time on page is measured while the recipient is
                              actually on the page.
                              <br />
                              The timer is stopped when:
                              <br />- the recipient switches tabs
                              <br />- the recipient switches windows
                              <br />- the window is not active
                              <br />- the window is active, but the screen
                              lock is on
                            </p>
                          }
                        >
                          <img src="/icons/info.svg" alt="" />
                        </CustomTooltip>
                      </span>
                    }
                  />
                </div>
                <div>
                <div className="d-flex cursor-pointer">
                  <Form.Check
                    className="laser-setting-popup kdf-view-second"
                    inline
                    name="notify_when"
                    type="radio"
                    id="notify-kdf-radio-notify_when_page"
                    value="notify_when_page"
                    checked={
                      editLaserLinkItem?.receipient_type ===
                      "notify_when_page"
                    }
                    onChange={(e) => {
                      if (e.target.value === "notify_when_page") {
                        const notifyForPage =
                          editLaserLinkItem?.notify_for_page;
                        const notifyPageAfter =
                          editLaserLinkItem?.notify_page_after;
                        const defaultPage =
                          !notifyForPage || Number(notifyForPage) < 1
                            ? 1
                            : notifyForPage;
                        const defaultSeconds =
                          !notifyPageAfter || Number(notifyPageAfter) < 1
                            ? 22
                            : notifyPageAfter;
                        updateEditLaserLinkData({
                          ...editLaserLinkItem,
                          receipient_type: e.target.value,
                          notify_for_page: defaultPage,
                          notify_page_after: defaultSeconds,
                          notify_after: "",
                        });
                        setIsError?.(false);
                        setFormErrorData((prev) =>
                          prev.filter(
                            (it) =>
                              it !== "notify_for_page" &&
                              it !== "notify_page_after"
                          )
                        );
                        receipientTypeViewNotifyPage.current?.focus();
                      } else {
                        updateEditLaserLinkData({
                          ...editLaserLinkItem,
                          receipient_type: e.target.value,
                        });
                      }
                    }}
                    label={
                      <span className="cursor-pointer">
                        views page &nbsp;
                        <CustomTooltip
                          title={
                            <p className="p-1 text-light">
                              Please enter a page between 1 and{" "}
                              {getDocFiles[0]?.num_pages ?? 0}
                            </p>
                          }
                        >
                          <input
                            className={`edit-box-input ${
                              formErrorData?.includes("notify_for_page")
                                ? "box-input-error"
                                : ""
                            }`}
                            type="number"
                            min="1"
                            max={getDocFiles[0]?.num_pages ?? undefined}
                            value={
                              editLaserLinkItem?.notify_for_page ?? ""
                            }
                            ref={receipientTypeViewNotifyPage}
                            onFocus={(e) => e.target.select()}
                            onChange={(e) => {
                              const inputValue = e.target.value;
                              const value = Number(inputValue);
                              const maxPages =
                                getDocFiles[0]?.num_pages ?? Infinity;

                              if (
                                inputValue === "" ||
                                (value >= 1 && value <= maxPages)
                              ) {
                                updateEditLaserLinkData({
                                  ...editLaserLinkItem,
                                  receipient_type: "notify_when_page",
                                  notify_for_page:
                                    inputValue === "" ? "" : value,
                                  notify_after: "",
                                });
                                if (
                                  inputValue !== "" &&
                                  value >= 1 &&
                                  value <= maxPages
                                ) {
                                  setIsError?.(false);
                                  setFormErrorData((prev) =>
                                    prev.filter(
                                      (it) => it !== "notify_for_page"
                                    )
                                  );
                                }
                              } else {
                                if (value > maxPages || value < 1) {
                                  if (
                                    !formErrorData?.includes(
                                      "notify_for_page"
                                    )
                                  ) {
                                    setIsError?.(true);
                                    setFormErrorData((prev) => [
                                      ...prev,
                                      "notify_for_page",
                                    ]);
                                  }
                                }
                              }
                            }}
                            onBlur={(e) => {
                              const inputValue = e.target.value;
                              if (
                                editLaserLinkItem?.receipient_type ===
                                "notify_when_page"
                              ) {
                                if (
                                  inputValue === "" ||
                                  Number(inputValue) < 1 ||
                                  Number(inputValue) >
                                    (getDocFiles[0]?.num_pages ?? Infinity)
                                ) {
                                  updateEditLaserLinkData({
                                    ...editLaserLinkItem,
                                    notify_for_page: 1,
                                  });
                                  setIsError?.(false);
                                  setFormErrorData((prev) =>
                                    prev.filter(
                                      (it) => it !== "notify_for_page"
                                    )
                                  );
                                }
                              }
                            }}
                            onClick={() =>
                              updateEditLaserLinkData({
                                ...editLaserLinkItem,
                                receipient_type: "notify_when_page",
                              })
                            }
                          />
                        </CustomTooltip> &nbsp;
                        for more than &nbsp;
                        <input
                          className={`edit-box-input ${
                            formErrorData?.includes("notify_page_after")
                              ? "box-input-error"
                              : ""
                          }`}
                          type="number"
                          min="1"
                          value={
                            editLaserLinkItem?.notify_page_after ?? ""
                          }
                          onFocus={(e) => e.target.select()}
                          onChange={(e) => {
                            const inputValue = e.target.value;
                            if (
                              inputValue === "" ||
                              Number(inputValue) >= 1
                            ) {
                              const numValue =
                                inputValue === ""
                                  ? ""
                                  : Number(inputValue);
                              updateEditLaserLinkData({
                                ...editLaserLinkItem,
                                receipient_type: "notify_when_page",
                                notify_page_after: numValue,
                                notify_after: "",
                              });
                              if (
                                numValue !== "" &&
                                Number(numValue) >= 1
                              ) {
                                setIsError?.(false);
                                setFormErrorData((prev) =>
                                  prev.filter(
                                    (it) => it !== "notify_page_after"
                                  )
                                );
                              }
                            } else {
                              if (
                                !formErrorData?.includes(
                                  "notify_page_after"
                                )
                              ) {
                                setIsError?.(true);
                                setFormErrorData((prev) => [
                                  ...prev,
                                  "notify_page_after",
                                ]);
                              }
                            }
                          }}
                          onBlur={(e) => {
                            const inputValue = e.target.value;
                            if (
                              editLaserLinkItem?.receipient_type ===
                              "notify_when_page"
                            ) {
                              if (
                                inputValue === "" ||
                                Number(inputValue) < 1
                              ) {
                                updateEditLaserLinkData({
                                  ...editLaserLinkItem,
                                  notify_page_after: 22,
                                });
                                setIsError?.(false);
                                setFormErrorData((prev) =>
                                  prev.filter(
                                    (it) => it !== "notify_page_after"
                                  )
                                );
                              }
                            }
                          }}
                          onClick={() =>
                            updateEditLaserLinkData({
                              ...editLaserLinkItem,
                              receipient_type: "notify_when_page",
                            })
                          }
                        /> &nbsp;
                        seconds
                        <CustomTooltip
                          placement="left"
                          title={
                            <p>
                              Time on page is measured while the recipient is
                              actually on the page.
                              <br />
                              The timer is stopped when:
                              <br />- the recipient switches tabs
                              <br />- the recipient switches windows
                              <br />- the window is not active
                              <br />- the window is active, but the screen
                              lock is on
                            </p>
                          }
                        >
                          <img src="/icons/info.svg" alt="" />
                        </CustomTooltip>
                      </span>
                    }
                  />
                </div>
                {formErrorData?.includes("notify_for_page") && hasValue(editLaserLinkItem?.notify_for_page) && (
                  <small className="text-danger notify-error-msg ms-4 ps-1">
                    Page must be between 1 and{" "}
                    {getDocFiles[0]?.num_pages ?? 0}.
                  </small>
                )}
                {formErrorData?.includes("notify_page_after") && hasValue(editLaserLinkItem?.notify_page_after) && (
                  <small className="text-danger notify-error-msg ms-4 ps-1">
                    Seconds must be at least 1.
                  </small>
                )}
                </div>
              </div>
            )}
        </>
      )}
    </div>
  );
};

export default SendNotificationTab;
